import _ from 'lodash'
import { t } from '@lingui/macro'
import { Paper, Stack, Typography } from '@mui/material'

import { Patient } from '@/core/models/patient.model'
import { useIsUS } from '@/hooks/useIsUS'
import { formatDateDMY, formatDateUS } from '@/utils/dateUtils'

import { FieldChoiceToggle } from './FieldChoiceToggle'

type MergeFieldItemProps = {
  fieldKey: string
  label: string
  primaryPatient: Patient
  secondaryPatient: Patient
}

export function MergeFieldItem({ fieldKey, label, primaryPatient, secondaryPatient }: MergeFieldItemProps) {
  const isUS = useIsUS()

  const fieldKeyWithUnderscore = fieldKey.split('_').join('.')
  const primaryValue = getFieldValue(primaryPatient, fieldKeyWithUnderscore, isUS)
  const secondaryValue = getFieldValue(secondaryPatient, fieldKeyWithUnderscore, isUS)

  if (!primaryValue && !secondaryValue) return null

  if (!primaryValue || !secondaryValue) {
    return <PartialValueField fieldKey={fieldKey} label={label} value={primaryValue || secondaryValue} />
  }

  const equal = _.isEqual(primaryValue, secondaryValue)

  if (equal && primaryValue) {
    return <EqualValueField fieldKey={fieldKey} label={label} primaryValue={primaryValue} />
  }

  return <FieldChoiceToggle fieldKey={fieldKey} label={label} primaryValue={primaryValue ?? ''} secondaryValue={secondaryValue ?? ''} />
}

function getFieldValue(patient: Patient, fieldKey: string, isUS: boolean): string | undefined {
  if (fieldKey === 'userInfo.fullName') {
    return `${_.get(patient, 'userInfo.name', '')} ${_.get(patient, 'userInfo.surname', '')}`.trim()
  }

  if (fieldKey === 'userInfo.gender') {
    const gender = _.get(patient, 'userInfo.gender')
    return gender === 'F' ? t`Female` : gender === 'M' ? t`Male` : gender === 'I' ? t`Unknown` : gender || ''
  }

  if (fieldKey === 'userInfo.birthday') {
    const birthday = _.get(patient, 'userInfo.birthday', '')
    return isUS ? formatDateUS(birthday) : formatDateDMY(birthday)
  }

  if (fieldKey === 'attendingPhysician.fullName') {
    const attendingPhysician = _.get(patient, 'attendingPhysician')
    if (!attendingPhysician) return undefined
    return `${_.get(attendingPhysician, 'firstname', '')} ${_.get(attendingPhysician, 'lastname', '')}`.trim() || undefined
  }

  return _.get(patient, fieldKey)
}

type EqualValueFieldProps = {
  fieldKey: string
  label: string
  primaryValue: string
}

export function EqualValueField({ fieldKey, label, primaryValue }: EqualValueFieldProps) {
  return (
    <Paper key={fieldKey} variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2">{primaryValue}</Typography>
      </Stack>
    </Paper>
  )
}

type PartialValueFieldProps = {
  fieldKey: string
  label: string
  value: string | undefined
}

export function PartialValueField({ fieldKey, label, value }: PartialValueFieldProps) {
  return (
    <Paper key={fieldKey} variant="outlined" sx={{ p: 2, bgcolor: 'info.lighter' }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2">{value}</Typography>
      </Stack>
    </Paper>
  )
}
